import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { browserCheck } from '@ta-interaktiv/browsercheck';
import { ReactSVG } from 'react-svg';
import Scrollchor from 'react-scrollchor';
import { select } from 'd3-selection';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0
THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.
See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var placeholderColor = '#EB0000';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') { return; }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "@font-face {\n  font-family: 'custom_icons';\n  src: url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.eot?5kiojs\");\n  src: url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.eot?5kiojs#iefix\") format(\"embedded-opentype\"), url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.ttf?5kiojs\") format(\"truetype\"), url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.woff?5kiojs\") format(\"woff\"), url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.svg?5kiojs#icomoon\") format(\"svg\");\n  font-weight: normal;\n  font-style: normal; }\n\n@font-face {\n  font-family: 'custom_icons';\n  src: url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.eot?5kiojs\");\n  src: url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.eot?5kiojs#iefix\") format(\"embedded-opentype\"), url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.ttf?5kiojs\") format(\"truetype\"), url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.woff?5kiojs\") format(\"woff\"), url(\"https://interaktiv.tagesanzeiger.ch/static/fonts/contenbricks-icons/icomoon.svg?5kiojs#icomoon\") format(\"svg\");\n  font-weight: normal;\n  font-style: italic; }\n\n.external-link:before {\n  font-family: 'custom_icons';\n  content: '\\e900';\n  margin-right: 0.25em; }\n\na.disco:hover,\na.disco:link,\na.disco:active,\na.disco:visited {\n  background-image: -webkit-gradient(linear, left top, right top, from(#ffe462), to(#ffe462));\n  background-image: -webkit-linear-gradient(left, #ffe462, #ffe462);\n  background-image: linear-gradient(90deg, #ffe462, #ffe462);\n  background-repeat: repeat-x;\n  background-position: bottom;\n  -webkit-background-size: 100% 20%;\n          background-size: 100% 20%;\n  -webkit-transition: all 0.1s ease-in-out;\n  transition: all 0.1s ease-in-out;\n  color: #202346;\n  -webkit-background-origin: content-box;\n          background-origin: content-box; }\n\na.disco:hover {\n  -webkit-background-size: 100% 100%;\n          background-size: 100% 100%; }\n";
styleInject(css_248z);

var Link = function (_a) {
    var _b = _a.href, href = _b === void 0 ? '' : _b, _c = _a.icon, icon = _c === void 0 ? 'icon-external-link' : _c, _d = _a.hideIcon, hideIcon = _d === void 0 ? false : _d, _e = _a.target, target = _e === void 0 ? ['_parent', '_blank'] : _e, _f = _a.className, className = _f === void 0 ? 'cbLink' : _f, _g = _a.style, style = _g === void 0 ? { whiteSpace: 'nowrap' } : _g, _h = _a.children, children = _h === void 0 ? 'Link' : _h, _j = _a.allowWordWrap, allowWordWrap = _j === void 0 ? false : _j, _k = _a.disco, disco = _k === void 0 ? false : _k, props = __rest(_a, ["href", "icon", "hideIcon", "target", "className", "style", "children", "allowWordWrap", "disco"]);
    if (children === 'Link' && href)
        children = href;
    var placeholderMode = children === 'Link';
    var noWrap = {
        whiteSpace: allowWordWrap ? 'normal' : 'nowrap'
    };
    var openIn;
    if (typeof target === 'object') {
        openIn = browserCheck.isMobile ? target[0] : target[1];
    }
    else {
        openIn = target;
    }
    return (React.createElement("a", __assign({ href: href, target: openIn, className: disco ? className + ' disco' : className, style: __assign(__assign(__assign({}, style), noWrap), (placeholderMode ? { color: placeholderColor } : {})) }, props),
        !hideIcon && (React.createElement("i", { className: 'icon ' + icon, style: { fontSize: '.8em' } })),
        children));
};
Link.defaultProps = {
    href: '',
    icon: 'external-link',
    hideIcon: false,
    target: ['_parent', '_blank'],
    className: 'cbLink',
    style: { whiteSpace: 'nowrap' },
    children: 'Link',
    allowWordWrap: false
};

var getAspectRatioPadding = function (ratioString) {
    var sides = ratioString.split(':');
    if (sides.length < 2)
        return '100%';
    return (+sides[1] * 100) / +sides[0] + '%';
};
var mdxPlacholderText = 'Als das Einhorn eines Morgens aus unruhigen Träumen erwachte, fand es sich in seinem Kopf ein kleines Niemalsstill beherbergend. Nun wusste das Einhorn nicht was ein Niemalsstill ist, geschweige denn wie es in seinen Kopf kam, daher frühstückte es erstmal ausgiebig. So aß das Einhorn zwölf Rüben, Joghurt und Quark bevor es daüber nachdachte warum statt Jux und Dollerei sein Kopf nun ein kleines Niemasstill beherbergte. Wie sollte es dieses Niemalsstill loswerden? Sollte ihm Viktor, ein Vogel aus Pforzheim, helfen können? Oder sollte es doch lieber flinke sylter Boxer damit beauftragen das quälend zwickende Niemalsstill zu verjagen? Das Einhorn wusste nicht was zu tun ist, dabei wollte es doch so gerne polyphone Xylophonmusik üben. Wie blöd, quäkte das Einhorn und legte sich wieder hin.';
var placeholderString = "data:image/svg+xml, %3Csvg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 500 500' xmlSpace='preserve' preserveAspectRatio='none' %3E%3Cg%3E%3Cpath d='M499,1v498H1V1H499 M500,0H0v500h500V0L500,0z' vector-effect='non-scaling-stroke' stroke-width='1px' stroke='%23888' /%3E%3Cline vector-effect='non-scaling-stroke' stroke-width='1px' stroke='%23888' x1='0' y1='0' x2='500' y2='500' /%3E%3Cline vector-effect='non-scaling-stroke' stroke-width='1px' stroke='%23888' x1='0' y1='500' x2='500' y2='0' /%3E%3C/g%3E%3C/svg%3E";

var Mdx = function (_a) {
    var file = _a.file, _b = _a.className, className = _b === void 0 ? 'cbMDX' : _b, _c = _a.noContainer, props = __rest(_a, ["file", "className", "noContainer"]);
    if (!file) {
        return React.createElement("div", { className: 'cbMdx cbPlaceholder' }, mdxPlacholderText);
    }
    var Content = file.default;
    return (React.createElement("div", { className: className },
        React.createElement(Content, __assign({}, props))));
};

var bordersChecked = {};
var ScrollTracking = function (_a) {
    var _b = _a.borders, borders = _b === void 0 ? [25, 50, 75] : _b, _c = _a.event, event = _c === void 0 ? 'Interactions' : _c, _d = _a.action, action = _d === void 0 ? 'vertical-scroll' : _d, _e = _a.contentPrefix, contentPrefix = _e === void 0 ? 'content' : _e, _f = _a.allowMulti, allowMulti = _f === void 0 ? false : _f;
    window.dataLayer = window.dataLayer || [];
    var _g = useState(0), currentPosition = _g[0], setcurrentPosition = _g[1];
    var _h = useState(0), lastPosition = _h[0], setlastPosition = _h[1];
    function useWindowSize() {
        useLayoutEffect(function () {
            function checkPosition() {
                var pos = window.pageYOffset ||
                    document.documentElement.scrollTop ||
                    document.body.scrollTop;
                var fullheight = (document.documentElement.scrollHeight ||
                    document.body.scrollHeight) - window.innerHeight;
                setcurrentPosition((pos * 100) / fullheight);
            }
            window.addEventListener('scroll', checkPosition);
            checkPosition();
            return function () { return window.removeEventListener('scroll', checkPosition); };
        }, []);
    }
    useEffect(function () {
        setlastPosition(currentPosition);
        borders.forEach(function (b) {
            if (lastPosition < b && currentPosition > b) {
                if (!bordersChecked['b' + b] || allowMulti) {
                    bordersChecked['b' + b] = true;
                    window.dataLayer.push({
                        event: event,
                        event_action: action,
                        event_label: contentPrefix + b
                    });
                }
            }
        });
    }, [currentPosition]);
    var scrollPos = useWindowSize();
    return null;
};

var css_248z$1 = ".cbImage {\n  margin-top: 0.5em;\n  margin-bottom: 0.75em;\n  font-family: var(--chart-font-stack); }\n  .cbImage figure {\n    margin: 0; }\n  .cbImage .title {\n    font-weight: 900;\n    font-size: 1.2em;\n    line-height: 1.4em; }\n  .cbImage .sub {\n    color: #888;\n    padding-bottom: 0.5em;\n    font-size: 0.9em;\n    line-height: 1.4em; }\n  .cbImage .source {\n    color: #bbb;\n    font-size: 0.75em; }\n  .cbImage.oversized {\n    margin-left: -15%;\n    width: 130%; }\n    @media only screen and (max-width: 599px) {\n      .cbImage.oversized {\n        margin-left: -2rem;\n        width: -webkit-calc(100% + 4rem);\n        width: calc(100% + 4rem); }\n        .cbImage.oversized .title,\n        .cbImage.oversized .sub,\n        .cbImage.oversized .source {\n          margin-left: 2rem; } }\n";
styleInject(css_248z$1);

var Image = function (_a) {
    var file = _a.file, alt = _a.alt, _b = _a.className, className = _b === void 0 ? 'cbImage' : _b, style = _a.style, title = _a.title, sub = _a.sub, source = _a.source, height = _a.height, aspectRatio = _a.aspectRatio, oversized = _a.oversized, children = _a.children, _c = _a.imageFirst, imageFirst = _c === void 0 ? false : _c, props = __rest(_a, ["file", "alt", "className", "style", "title", "sub", "source", "height", "aspectRatio", "oversized", "children", "imageFirst"]);
    var src = '';
    var h = 'auto';
    if (file) {
        if (typeof file === 'object') {
            if (browserCheck.isMobile) {
                src = file[0].toString();
            }
            else {
                src = file[1].toString();
            }
        }
        else {
            src = file.toString();
        }
    }
    if (typeof height === 'object') {
        if (browserCheck.isMobile) {
            h = height[0];
        }
        else {
            h = height[1];
        }
    }
    else {
        h = height || '100%';
    }
    var ImgElement = function (_a) {
        var imageSrc = _a.imageSrc;
        if (!imageSrc || imageSrc === '') {
            return (React.createElement("img", { src: placeholderString, style: __assign({
                    position: 'absolute',
                    width: '100%',
                    height: '100%'
                }) }));
        }
        return (React.createElement("img", __assign({ src: imageSrc, className: 'image', style: __assign(__assign({}, style), (aspectRatio || height
                ? {
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover'
                }
                : { width: '100%' })), alt: alt, role: alt ? undefined : 'presentation' }, props)));
    };
    var titleElement = React.createElement("div", { className: 'title' }, title);
    var subElement = React.createElement("div", { className: 'sub' }, sub);
    var sourceElement = React.createElement("div", { className: 'source' }, source);
    if (src === '' && !height && !aspectRatio)
        aspectRatio = '16:9';
    return (React.createElement("div", { className: className + ' ' + (oversized ? 'oversized' : '') },
        React.createElement("figure", null,
            !imageFirst && (React.createElement("figcaption", null,
                title && titleElement,
                sub && subElement)),
            children && !imageFirst && children,
            React.createElement("div", { style: aspectRatio || height
                    ? {
                        paddingBottom: aspectRatio
                            ? getAspectRatioPadding(aspectRatio)
                            : h,
                        position: 'relative',
                        lineHeight: '0'
                    }
                    : { lineHeight: '0' } },
                React.createElement(ImgElement, { imageSrc: src })),
            children && imageFirst && children,
            imageFirst && (React.createElement("figcaption", null,
                title && titleElement,
                sub && subElement,
                source && sourceElement)),
            source && !imageFirst && sourceElement)));
};

var css_248z$2 = ".cbSVG {\n  margin-top: 0.5em;\n  margin-bottom: 0.75em;\n  font-family: var(--chart-font-stack); }\n  .cbSVG figure {\n    margin: 0; }\n  .cbSVG .title {\n    font-weight: 900;\n    font-size: 1.2em;\n    line-height: 1.4em; }\n  .cbSVG .sub {\n    color: #888;\n    padding-bottom: 0.5em;\n    font-size: 0.9em;\n    line-height: 1.4em; }\n  .cbSVG .source {\n    color: #bbb;\n    font-size: 0.75em; }\n  .cbSVG .svgContainer {\n    line-height: 0; }\n  .cbSVG .image svg {\n    resize: 'horizontal';\n    position: absolute;\n    left: 50%;\n    -webkit-transform: translateX(-50%);\n    transform: translateX(-50%);\n    height: 100%;\n    max-width: 100%; }\n  .cbSVG.oversized {\n    margin-left: -15%;\n    width: 130%; }\n    @media only screen and (max-width: 599px) {\n      .cbSVG.oversized {\n        margin-left: -2rem;\n        width: -webkit-calc(100% + 4rem);\n        width: calc(100% + 4rem); }\n        .cbSVG.oversized .title,\n        .cbSVG.oversized .sub,\n        .cbSVG.oversized .source {\n          margin-left: 2rem; } }\n";
styleInject(css_248z$2);

var CompFromString = function (_a) {
    var src = _a.src;
    return (React.createElement("div", { dangerouslySetInnerHTML: {
            __html: atob(src.replace(/data:image\/svg\+xml;base64,/, ''))
        } }));
};
var InlineSvg = function (_a) {
    var file = _a.file, className = _a.className, style = _a.style, title = _a.title, sub = _a.sub, source = _a.source, height = _a.height, aspectRatio = _a.aspectRatio, oversized = _a.oversized, children = _a.children, imageFirst = _a.imageFirst, props = __rest(_a, ["file", "className", "style", "title", "sub", "source", "height", "aspectRatio", "oversized", "children", "imageFirst"]);
    var src = '';
    var h = 'auto';
    if (file) {
        if (typeof file === 'object') {
            if (browserCheck.isMobile) {
                src = file[0].toString();
            }
            else {
                src = file[1].toString();
            }
        }
        else {
            src = file.toString();
        }
        if (!file.toString().includes('base64') && !file.toString().includes('/')) {
            src = 'images/' + src;
        }
    }
    if (typeof height === 'object') {
        if (browserCheck.isMobile) {
            h = height[0];
        }
        else {
            h = height[1];
        }
    }
    else {
        h = height || '100%';
    }
    var ImgElement = function (_a) {
        var imageSrc = _a.imageSrc;
        if (!imageSrc || imageSrc === '') {
            return (React.createElement("img", { src: placeholderString, style: __assign({
                    position: 'absolute',
                    width: '100%',
                    height: '100%'
                }) }));
        }
        return (React.createElement("img", __assign({ src: src, className: 'image', style: __assign(__assign({}, style), (aspectRatio || height
                ? {
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover'
                }
                : { width: '100%' })) }, props)));
    };
    var doAfterInlineSVGInjection = function (err, svg) {
        if (svg) {
            if (svg &&
                svg.parentElement &&
                svg.parentElement.parentElement &&
                oversized) {
                svg.parentElement.parentElement.removeAttribute('style');
            }
            svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            if (h || aspectRatio) {
                svg.setAttribute('width', '100%');
                svg.setAttribute('height', h);
            }
        }
        if (err) {
            console.warn('contentbricks | SVG \n', err);
        }
    };
    if (src === '' && !height && !aspectRatio)
        aspectRatio = '16:9';
    var titleElement = React.createElement("div", { className: 'title' }, title);
    var subElement = React.createElement("div", { className: 'sub' }, sub);
    var sourceElement = React.createElement("div", { className: 'source' }, source);
    return (React.createElement("div", { className: className + ' ' + (oversized ? 'oversized' : '') },
        React.createElement("figure", null,
            !imageFirst && (React.createElement("figcaption", null,
                title && titleElement,
                sub && subElement)),
            children && !imageFirst && children,
            src.includes('data:image/svg+xml;base64,') ? (React.createElement(CompFromString, { src: src })) : (React.createElement(ReactSVG, __assign({ src: src, className: height || aspectRatio ? 'image svgContainer' : 'svgContainer', wrapper: 'div' }, props, { style: __assign(__assign({}, style), (aspectRatio || height
                    ? {
                        position: 'relative',
                        top: '0',
                        left: '0',
                        right: '0',
                        bottom: '0',
                        height: h || '100%',
                        width: '100%',
                        objectFit: 'cover',
                        paddingBottom: aspectRatio
                            ? getAspectRatioPadding(aspectRatio)
                            : '0'
                    }
                    : { width: '100%' })), afterInjection: doAfterInlineSVGInjection, fallback: function () { return React.createElement(ImgElement, { imageSrc: src }); } }))),
            children && imageFirst && children,
            imageFirst && (React.createElement("figcaption", null,
                title && titleElement,
                sub && subElement,
                source && sourceElement)),
            source && !imageFirst && sourceElement)));
};
InlineSvg.defaultProps = {
    className: 'cbSVG',
    oversized: false
};

var css_248z$3 = ".cbPlaceholder {\n  letter-spacing: 0.4px;\n  width: 100%;\n  border: 1px dashed lightgray;\n  padding: 10px;\n  color: #000;\n  color: lightgray; }\n  .cbPlaceholder .placeholderTitle {\n    position: absolute;\n    background-color: #000;\n    padding: 10px;\n    color: #fff; }\n\n.cbChapterNavigation,\n.cbChapterNavigation.cbPlaceholder {\n  margin-top: 2em !important;\n  font-family: var(--chart-font-stack) !important; }\n  .cbChapterNavigation .title,\n  .cbChapterNavigation.cbPlaceholder .title {\n    font-weight: 900;\n    font-size: 1.2em; }\n  .cbChapterNavigation .entry .chapter,\n  .cbChapterNavigation.cbPlaceholder .entry .chapter {\n    font-size: 0.9em;\n    padding-top: 0.5em;\n    padding-bottom: 0.5em;\n    border-bottom: 1px solid #eee;\n    display: inline-block;\n    width: 100%; }\n    .cbChapterNavigation .entry .chapter i,\n    .cbChapterNavigation.cbPlaceholder .entry .chapter i {\n      font-size: 0.8em;\n      margin-top: 0.2em;\n      height: 100%; }\n  .cbChapterNavigation .entry .name,\n  .cbChapterNavigation.cbPlaceholder .entry .name {\n    display: inline-block;\n    width: -webkit-calc(100% - 25px);\n    width: calc(100% - 25px); }\n  .cbChapterNavigation .entry .icon,\n  .cbChapterNavigation.cbPlaceholder .entry .icon {\n    float: right;\n    max-width: 25px;\n    height: 100%; }\n  .cbChapterNavigation .entry:last-of-type .chapter,\n  .cbChapterNavigation.cbPlaceholder .entry:last-of-type .chapter {\n    border-bottom: 0; }\n  .cbChapterNavigation.cbPlaceholder .chapter,\n  .cbChapterNavigation.cbPlaceholder.cbPlaceholder .chapter {\n    color: lightgray; }\n  .cbChapterNavigation.cbPlaceholder .title,\n  .cbChapterNavigation.cbPlaceholder.cbPlaceholder .title {\n    color: lightgray; }\n\n.cbMdx.cbPlaceholder {\n  color: gray; }\n";
styleInject(css_248z$3);

var ChapterNavigation = function (_a) {
    var content = _a.content, title = _a.title, hideFirstEntry = _a.hideFirstEntry, hideArrows = _a.hideArrows, _b = _a.className, className = _b === void 0 ? 'cbChapterNavigation' : _b, jump = _a.jump;
    if (!content) {
        return (React.createElement("div", { className: 'cbChapterNavigation cbPlaceholder' },
            React.createElement("div", { className: 'title' }, title || 'Kapitel-Navigation'),
            React.createElement("div", { className: 'entry' },
                React.createElement("div", { className: 'chapter' },
                    React.createElement("div", { className: 'name' }, "Kapitel 1"),
                    !hideArrows && (React.createElement("div", { className: 'icon' },
                        React.createElement("i", { className: 'arrow alternate down icon' }))))),
            React.createElement("div", { className: 'entry' },
                React.createElement("div", { className: 'chapter' },
                    React.createElement("div", { className: 'name' }, "Kapitel 2"),
                    !hideArrows && (React.createElement("div", { className: 'icon' },
                        React.createElement("i", { className: 'arrow alternate down icon' }))))),
            React.createElement("div", { className: 'entry' },
                React.createElement("div", { className: 'chapter' },
                    React.createElement("div", { className: 'name' }, "Kapitel 3"),
                    !hideArrows && (React.createElement("div", { className: 'icon' },
                        React.createElement("i", { className: 'arrow alternate down icon' })))))));
    }
    return (React.createElement("div", { className: className },
        title && React.createElement("div", { className: 'title' }, title),
        content.map(function (chapter, i) {
            if (i === 0 && hideFirstEntry)
                return;
            return (React.createElement(Scrollchor, { to: chapter.id, key: i, className: 'entry' },
                React.createElement("div", { className: 'chapter' },
                    React.createElement("div", { className: 'name' }, chapter.name),
                    !hideArrows && (React.createElement("div", { className: 'icon' },
                        React.createElement("i", { className: 'arrow alternate down icon' }))))));
        })));
};

var css_248z$4 = ".cbAnnotationLayer {\n  overflow: hidden;\n  font-size: 12px; }\n  .cbAnnotationLayer .wrapper {\n    width: 100%;\n    height: 100%;\n    position: relative;\n    line-height: 0;\n    padding: 0;\n    margin: 0; }\n  .cbAnnotationLayer img {\n    width: 100%;\n    height: auto; }\n  .cbAnnotationLayer div.overlayContainer {\n    height: 0;\n    width: 100%;\n    width: 100%;\n    height: 100%;\n    position: absolute;\n    top: 0;\n    left: 0; }\n  .cbAnnotationLayer svg.overlay {\n    width: 100%;\n    height: 100%;\n    position: absolute;\n    top: 0;\n    left: 0;\n    font-family: var(--chart-font-stack);\n    font-weight: 400;\n    font-size: 1.4vw; }\n    @media only screen and (max-width: 599px) {\n      .cbAnnotationLayer svg.overlay {\n        font-size: 2.5vw; } }\n    @media only screen and (min-width: 1200px) {\n      .cbAnnotationLayer svg.overlay {\n        font-size: 0.9vw; } }\n    @media only screen and (min-width: 1500px) {\n      .cbAnnotationLayer svg.overlay {\n        font-size: 0.8vw; } }\n    .cbAnnotationLayer svg.overlay .bold,\n    .cbAnnotationLayer svg.overlay .red {\n      font-weight: 900;\n      font-size: 1.1em; }\n    .cbAnnotationLayer svg.overlay .red {\n      fill: #fff; }\n    .cbAnnotationLayer svg.overlay path {\n      stroke: rgba(0, 0, 0, 0.5);\n      stroke-width: 0.75px; }\n  .cbAnnotationLayer svg.locator:hover {\n    cursor: pointer; }\n  .cbAnnotationLayer .locator:not(svg) {\n    position: absolute;\n    background-color: white;\n    padding: 13px;\n    display: inline-block; }\n  .cbAnnotationLayer .locatorLine {\n    stroke: rgba(0, 0, 0, 0.4);\n    stroke-width: 0.5; }\n  .cbAnnotationLayer .locatorLineText {\n    font-size: 10px;\n    fill: rgba(0, 0, 0, 0.6); }\n  .cbAnnotationLayer .center {\n    position: relative;\n    top: -webkit-calc(50% - 1.5px);\n    top: calc(50% - 1.5px);\n    left: -webkit-calc(50% - 1.5px);\n    left: calc(50% - 1.5px);\n    width: 3px;\n    height: 3px;\n    background-color: #f00;\n    display: inline-block; }\n";
styleInject(css_248z$4);

var percentToPixel = function (convert, hundred) {
    return (convert * hundred) / 100;
};

var ArrowGenerator = function (offset, svgSize, linePadding, lineHeight, type) {
    var x = percentToPixel(offset.x, svgSize[0]);
    var y = percentToPixel(offset.y, svgSize[1]);
    var xStart = 0;
    var yStart = 0;
    if (Math.abs(offset.x) < Math.abs(offset.y)) {
        yStart = y < 0 ? -linePadding : linePadding;
        yStart -= y < 0 ? lineHeight : -lineHeight * 1.5;
    }
    else {
        xStart = x < 0 ? -linePadding : linePadding;
        if (type === 'curves' && offset.y !== 0) {
            yStart -= y < 0 ? lineHeight : -lineHeight;
        }
    }
    var pathVersions = {
        flows: 'M' +
            xStart +
            ' ' +
            yStart +
            ' C  ' +
            x * 0.3 +
            ' ' +
            yStart +
            ', ' +
            (x - x * 0.6) +
            ' ' +
            y +
            ', ' +
            x +
            ',' +
            y,
        straight: 'M' + xStart + ' ' + yStart + ' L' + x + ',' + y,
        curves: 'M' +
            xStart +
            ' ' +
            yStart +
            ' C  ' +
            xStart +
            ' ' +
            (y - y * 0.5) +
            ', ' +
            (x - x * 0.5) +
            ' ' +
            y +
            ', ' +
            x +
            ',' +
            y
    };
    if (Math.abs(offset.x) < 1)
        return pathVersions.straight;
    return pathVersions[type];
};

var TextField = function (_a) {
    var text = _a.text, align = _a.align, lineHeight = _a.lineHeight, _b = _a.maxAnnotationWidth, maxAnnotationWidth = _b === void 0 ? 150 : _b, setLineCount = _a.setLineCount;
    var textnode = useRef(null);
    useEffect(function () {
        var splitedText = text.split(/\s+/);
        var targetText = select(textnode.current);
        var testEL = document.createElement('svg');
        var testTextEL = document.createElement('svg');
        testTextEL = testEL.appendChild(testTextEL);
        var testSVG = select(testEL);
        var testText = testSVG.append('text');
        var textInLines = [];
        var line = [];
        document.body.appendChild(testEL);
        for (var i = 0; i < splitedText.length; i++) {
            line.push(splitedText[i]);
            testText.html(line.join(' '));
            var currentWidth = testTextEL.getBoundingClientRect().width;
            if (currentWidth > maxAnnotationWidth) {
                line.pop();
                textInLines.push(line);
                line = [splitedText[i]];
            }
        }
        textInLines.push(line);
        testSVG.remove();
        if (setLineCount)
            setLineCount(textInLines.length);
        targetText.html('');
        for (var i = 0; i < textInLines.length; i++) {
            targetText
                .append('tspan')
                .attr('x', 0)
                .attr('dy', i === 0 ? 0 : lineHeight)
                .html(textInLines[i].join(' '));
        }
    }, []);
    return (React.createElement("text", { ref: textnode, textRendering: 'optimizeLegibility', textAnchor: align }));
};

var BaseAnnotationComp = function (_a) {
    var content = _a.content, _b = _a.align, align = _b === void 0 ? 'start' : _b, lineHeight = _a.lineHeight;
    return (React.createElement(TextField, { text: content.toString(), align: align, lineHeight: lineHeight }));
};

var OffsetAnnotation = function (_a) {
    var position = _a.position, content = _a.content, _b = _a.svgSize, svgSize = _b === void 0 ? [1, 0] : _b, _c = _a.offset, offset = _c === void 0 ? [0, 0] : _c, alignOverwrite = _a.alignOverwrite, _d = _a.Comp, Comp = _d === void 0 ? BaseAnnotationComp : _d, lineHeight = _a.lineHeight, linePadding = _a.linePadding, hideMarker = _a.hideMarker, _e = _a.lineFlow, lineFlow = _e === void 0 ? 'flows' : _e, marker = _a.marker;
    var x = percentToPixel(position[0], svgSize[0]);
    var y = percentToPixel(position[1], svgSize[1]);
    var offX = percentToPixel(offset[0], svgSize[0]);
    var offY = percentToPixel(offset[1], svgSize[1]);
    var align = 'start';
    if (x >= x + offX)
        align = 'end';
    if (alignOverwrite)
        align = alignOverwrite;
    var pathString = ArrowGenerator({ x: -offset[0], y: -offset[1] }, svgSize, linePadding || 0, lineHeight, lineFlow);
    return (React.createElement("g", { transform: 'translate(' + x + ',' + y + ')' },
        React.createElement("g", { transform: 'translate(' + offX + ',' + offY + ') scale(' + 1 + ')' },
            React.createElement(Comp, { content: content, align: align, lineHeight: lineHeight })),
        (offset[0] || offset[1]) && (React.createElement("g", { transform: 'translate(' + offX + ',' + offY + ')' },
            React.createElement("path", { d: pathString, className: 'arrow', fill: 'none', markerEnd: !hideMarker ? marker && 'url(#' + marker[1] + ')' : undefined, markerStart: !hideMarker ? marker && 'url(#' + marker[0] + ')' : undefined }),
            "}"))));
};

var AnnotationLayer = function (_a) {
    var id = _a.id, annotations = _a.annotations, AnnotationComponent = _a.AnnotationComponent, _b = _a.lineHeight, lineHeight = _b === void 0 ? 17 : _b, _c = _a.linePadding, linePadding = _c === void 0 ? 2 : _c, _d = _a.hideMarker, hideMarker = _d === void 0 ? false : _d, _e = _a.locator, locator = _e === void 0 ? false : _e, _f = _a.lineFlow, lineFlow = _f === void 0 ? 'flows' : _f, _g = _a.marker, marker = _g === void 0 ? ['none', 'dot'] : _g, children = _a.children;
    var wrapper = useRef(null);
    var svg = useRef(null);
    var _h = useState(false), loaded = _h[0], setLoaded = _h[1];
    var _j = useState([0, 0]), svgSize = _j[0], setSvgSize = _j[1];
    var _k = useState([0, 0]), locatorPos = _k[0], setLocatorPos = _k[1];
    var _l = useState(undefined), locatorValue = _l[0], setLocatorValue = _l[1];
    var _m = useState('0 0 0 0'), viewBox = _m[0], setviewBox = _m[1];
    var gridValues = [10, 20, 30, 40, 50, 60, 70, 80, 90];
    var changeHeight = function (wrapper) {
        if (wrapper && wrapper.current) {
            if (wrapper.current.clientHeight > 0) {
                setSvgSize([
                    wrapper.current.clientWidth || 0,
                    wrapper.current.clientHeight || 0
                ]);
                setviewBox('0 0 ' +
                    wrapper.current.clientWidth +
                    ' ' +
                    wrapper.current.clientHeight);
            }
        }
        else {
            changeHeight(wrapper);
        }
    };
    var handleMouseOut = function () {
        setLocatorPos([2000, 2000]);
    };
    var handleMouseover = function (e) {
        if (e) {
            if (e.buttons === 0) {
                setLocatorPos([e.offsetX, e.offsetY]);
                setLocatorValue(((e.offsetX * 100) / svgSize[0]).toFixed(0) +
                    ',' +
                    ((e.offsetY * 100) / svgSize[1]).toFixed(0));
            }
        }
    };
    useEffect(function () {
        if (wrapper && wrapper.current) {
            if (wrapper.current.clientHeight > 0) {
                changeHeight(wrapper);
            }
            else {
                changeHeight(wrapper);
            }
        }
    }, [loaded]);
    useEffect(function () {
        if (svg && svg.current) {
            if (locator) {
                svg.current.addEventListener('mousemove', handleMouseover);
                svg.current.addEventListener('mouseleave', handleMouseOut);
            }
        }
        return function () {
            if (svg && svg.current) {
                svg.current.removeEventListener('mousemove', handleMouseover);
                svg.current.addEventListener('mouseleave', handleMouseOut);
            }
        };
    }, [svgSize]);
    return (React.createElement("div", { className: 'cbAnnotationLayer', id: id && id },
        React.createElement("figure", { className: 'wrapper', ref: wrapper, onLoad: function () {
                setLoaded(true);
            } },
            children,
            React.createElement("div", { className: 'overlayContainer' },
                React.createElement("svg", { ref: svg, className: locator ? 'overlay locator' : 'overlay', version: '1.1', xmlns: 'http://www.w3.org/2000/svg', xmlnsXlink: 'http://www.w3.org/1999/xlink', x: '0px', y: '0px', viewBox: viewBox, xmlSpace: 'preserve', preserveAspectRatio: 'xMidYMid meet' },
                    React.createElement("defs", null,
                        React.createElement("marker", { id: 'arrow', viewBox: '0 0 10 10', refX: '5', refY: '5', markerWidth: '6', markerHeight: '6', orient: 'auto-start-reverse' },
                            React.createElement("path", { d: 'M 0 0 L 10 5 L 0 10 z' })),
                        React.createElement("marker", { id: 'dot', viewBox: '0 0 10 10', refX: '5', refY: '5', markerWidth: '6', markerHeight: '6', orient: 'auto-start-reverse' },
                            React.createElement("circle", { cx: '5', cy: '5', r: '4', fill: 'black' }))),
                    svgSize[1] !== 0 &&
                        annotations &&
                        annotations.map(function (anno, i) {
                            return (React.createElement(OffsetAnnotation, { key: i, content: anno.content, position: [anno.x, anno.y], offset: [anno.offsetX || 0, anno.offsetY || 0], svgSize: svgSize, alignOverwrite: anno.align, Comp: AnnotationComponent, lineHeight: lineHeight, linePadding: linePadding, hideMarker: hideMarker, lineFlow: lineFlow, marker: marker }));
                        }),
                    locator && (React.createElement(React.Fragment, null,
                        React.createElement("g", null, gridValues.map(function (g, i) {
                            return (React.createElement(React.Fragment, { key: i },
                                React.createElement("line", { x1: 0, x2: svgSize[0], y1: (svgSize[1] * g) / 100, y2: (svgSize[1] * g) / 100, className: 'locatorLine' }),
                                React.createElement("text", { x: '5', y: (svgSize[1] * g) / 100 - 5, className: 'locatorLineText' }, g),
                                React.createElement("line", { y1: 0, y2: svgSize[1], x1: (svgSize[0] * g) / 100, x2: (svgSize[0] * g) / 100, className: 'locatorLine' }),
                                React.createElement("text", { y: '15', x: (svgSize[0] * g) / 100 + 5, className: 'locatorLineText' }, g)));
                        })),
                        locatorValue && (React.createElement("g", { transform: 'translate(' +
                                locatorPos[0] +
                                ',' +
                                (locatorPos[1] + 30) +
                                ')' },
                            React.createElement("rect", { x: '0', y: '-20', width: '50', height: '30', fill: '#fff' }),
                            React.createElement("text", { x: '5' }, locatorValue))))))))));
};

export { AnnotationLayer, ChapterNavigation, Image, InlineSvg, Link, Mdx, ScrollTracking };
//# sourceMappingURL=index.js.map
